// DashTerminPopover.tsx — Klick-Popover eines Graph-Termins in der Dashboard-
// Termine-Karte (Dashboard-Termine-Review SP5, A10): voller Betreff, Datum +
// Uhrzeit (bzw. „Ganztägig"), Ort, Online-Badge, Einladungs-Richtung
// („Du hast eingeladen" / „Eingeladen von X"), reduzierte Teilnehmerliste
// (max. 5 + „+N weitere") und der Footer-Link „Im Kalender öffnen" (Touch-Ersatz
// für den Doppelklick). Alle Daten stammen aus der Listen-Antwort — KEIN
// Zusatz-Fetch.
//
// Portal an document.body mit position:fixed am Klick-Anker, damit das Popover
// NICHT vom overflow:hidden der DashCard beschnitten wird. Styling rein inline
// über REFINED-Tokens (keine .kvp-rd-Klasse → keine Portal-min-height-Falle
// #648). Die Position wird über clampPopoverPosition (calendar/peek.ts) in den
// Viewport geklemmt — geteilte, getestete Logik (kein Reimplement).

import * as React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { Dismiss16Regular, Open16Regular, Video16Regular } from "@fluentui/react-icons";
import type { SourcedCalendarEvent } from "../../services/calendarService";
import { clampPopoverPosition, type PeekAnchor } from "../calendar/peek";
import { REFINED } from "../risikoanalyseStep/tokens";
import { toGraphDate, deTime } from "./DashboardShared";
import { einladungsRichtung, reduzierteTeilnehmer } from "./terminPeek";
import { alleFenster } from "../windowManager/browserFenster/fensterRegister";

/** Ein offener Peek: Event + Anker (null ⇒ Viewport-Mitte). */
export interface DashTerminPeek {
    event: SourcedCalendarEvent;
    anchor: PeekAnchor | null;
}

/** Datum + Uhrzeit fürs Popover — „Ganztägig, <Tag>" bzw. „<Tag>, HH:MM – HH:MM". */
const zeitLabel = (ev: SourcedCalendarEvent): string => {
    const tag = toGraphDate(ev.start.dateTime).toLocaleDateString("de-DE", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
    if (ev.isAllDay) return `Ganztägig, ${tag}`;
    return `${tag}, ${deTime(ev.start.dateTime)} – ${deTime(ev.end.dateTime)}`;
};

const labelStyle: React.CSSProperties = {
    fontSize: "0.72rem",
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: REFINED.ink3,
    marginBottom: 2,
};

export const DashTerminPopover: React.FC<{
    peek: DashTerminPeek | null;
    /** Eigene Postfach-Adresse (aus user.email) — steuert die Einladungs-Richtung. */
    ownAddress: string;
    onClose: () => void;
    /** „Im Kalender öffnen" — identische Aktion wie der Doppelklick (Touch-Ersatz). */
    onOpenInCalendar: (ev: SourcedCalendarEvent) => void;
}> = ({ peek, ownAddress, onClose, onOpenInCalendar }) => {
    const popRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

    // Nach dem Render die echte Größe messen und am Anker in den Viewport klemmen
    // (bis dahin unsichtbar — kein Sprung-Flackern).
    useLayoutEffect(() => {
        if (!peek) {
            setPos(null);
            return;
        }
        const el = popRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const anchor: PeekAnchor = peek.anchor ?? {
            x: Math.round(window.innerWidth / 2 - r.width / 2),
            y: Math.round(window.innerHeight / 2 - r.height / 2),
        };
        setPos(
            clampPopoverPosition(
                anchor,
                { width: r.width, height: r.height },
                { width: window.innerWidth, height: window.innerHeight },
            ),
        );
    }, [peek]);

    // Außenklick/Escape/Scroll/Resize schließen. Der öffnende Klick liegt zeitlich
    // vor dem Öffnen (250 ms-Entprellung in der Karte) → schließt nicht sofort.
    useEffect(() => {
        if (!peek) return;
        const onDown = (e: MouseEvent) => {
            if (popRef.current?.contains(e.target as Node)) return;
            onClose();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        const onReflow = () => onClose();
        alleFenster.addEventListener("pointerdown", onDown);
        alleFenster.addEventListener("keydown", onKey);
        alleFenster.addEventListener("resize", onReflow);
        alleFenster.addEventListener("scroll", onReflow, true); // capture: auch innere Scroller
        return () => {
            alleFenster.removeEventListener("pointerdown", onDown);
            alleFenster.removeEventListener("keydown", onKey);
            alleFenster.removeEventListener("resize", onReflow);
            alleFenster.removeEventListener("scroll", onReflow, true);
        };
    }, [peek, onClose]);

    if (!peek) return null;
    const ev = peek.event;
    const richtung = einladungsRichtung(ev.organizer, ownAddress);
    const { namen, rest } = reduzierteTeilnehmer(ev.attendees, ownAddress);

    return createPortal(
        <div
            ref={popRef}
            role="dialog"
            aria-label={ev.subject || "Termin"}
            style={{
                position: "fixed",
                top: pos?.top ?? -9999,
                left: pos?.left ?? -9999,
                visibility: pos ? "visible" : "hidden",
                width: 320,
                maxWidth: "calc(100vw - 16px)",
                boxSizing: "border-box",
                backgroundColor: REFINED.white,
                border: `1px solid ${REFINED.line}`,
                borderRadius: 14,
                boxShadow: "0 12px 32px rgba(58,12,49,0.18)",
                padding: 16,
                zIndex: 4000,
                fontFamily: REFINED.fontUI,
            }}
        >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <h3
                    style={{
                        margin: 0,
                        flex: 1,
                        minWidth: 0,
                        fontSize: "1rem",
                        fontWeight: 800,
                        color: REFINED.ink,
                        lineHeight: 1.25,
                        overflowWrap: "anywhere",
                    }}
                >
                    {ev.subject || "(ohne Titel)"}
                </h3>
                <button
                    type="button"
                    aria-label="Schließen"
                    onClick={onClose}
                    style={{
                        flexShrink: 0,
                        width: 28,
                        height: 28,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 999,
                        border: "none",
                        background: "transparent",
                        color: REFINED.ink3,
                        cursor: "pointer",
                        padding: 0,
                    }}
                >
                    <Dismiss16Regular />
                </button>
            </div>

            <div style={{ marginTop: 6, fontSize: "0.86rem", fontWeight: 700, color: REFINED.ink2 }}>
                {zeitLabel(ev)}
            </div>

            {ev.location && (
                <div
                    style={{
                        marginTop: 6,
                        fontSize: "0.84rem",
                        color: REFINED.ink2,
                        overflowWrap: "anywhere",
                    }}
                >
                    {ev.location}
                </div>
            )}

            {ev.isOnlineMeeting && (
                <div style={{ marginTop: 10 }}>
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "3px 10px",
                            borderRadius: 999,
                            backgroundColor: REFINED.aub50,
                            color: REFINED.aub,
                            fontSize: "0.74rem",
                            fontWeight: 800,
                            whiteSpace: "nowrap",
                        }}
                    >
                        <Video16Regular /> Online-Meeting
                    </span>
                </div>
            )}

            {richtung && (
                <div style={{ marginTop: 12 }}>
                    <div style={labelStyle}>Einladung</div>
                    <div style={{ fontSize: "0.88rem", color: REFINED.ink }}>{richtung}</div>
                </div>
            )}

            {namen.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <div style={labelStyle}>Teilnehmer</div>
                    <div style={{ fontSize: "0.88rem", color: REFINED.ink2, lineHeight: 1.35 }}>
                        {namen.join(", ")}
                        {rest > 0 ? ` +${rest} weitere` : ""}
                    </div>
                </div>
            )}

            <button
                type="button"
                onClick={() => onOpenInCalendar(ev)}
                style={{
                    marginTop: 16,
                    width: "100%",
                    minHeight: 40,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    borderRadius: 12,
                    border: `1px solid ${REFINED.line}`,
                    background: REFINED.white,
                    color: REFINED.aub,
                    cursor: "pointer",
                    fontFamily: REFINED.fontUI,
                    fontSize: "0.88rem",
                    fontWeight: 800,
                }}
            >
                <Open16Regular /> Im Kalender öffnen
            </button>
        </div>,
        document.body,
    );
};

export default DashTerminPopover;
